import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { getNamespaceFromPath } from '@/i18n';

const Input = React.forwardRef(
	(
		{
			loginPassword,
			readOnly,
			label,
			className,
			inputClassName,
			labelClassName,
			type,
			error,
			value,
			placeholder,
			icon: Icon,
			required,
			...props
		},
		ref,
	) => {
		const { t } = useTranslation(getNamespaceFromPath());
		const [showPassword, setShowPassword] = useState(false);

		const isPassword = type === 'password';

		const renderLabel = () => {
			if (!label) return null;

			const labelElement = (
				<label className={cn('block font-medium text-foreground', labelClassName)}>
					{t(label)}
					{required && <span className="text-destructive"> *</span>}
				</label>
			);

			if (loginPassword) {
				return (
					<div className="flex justify-between">
						{labelElement}
						<div className="text-sm">
							<a
								href="#"
								className="font-medium text-primary"
							>
								{t('Forgot your password?')}
							</a>
						</div>
					</div>
				);
			}

			return labelElement;
		};

		return (
			<div className={cn('space-y-1', className)}>
				{renderLabel()}

				<div className="relative w-full">
					<input
						data-slot="input"
						readOnly={readOnly}
						type={isPassword && showPassword ? 'text' : type}
						className={cn(
							'flex h-10 w-full rounded-md border border-input bg-background text-foreground px-3 py-2 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:border-ring focus:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
							isPassword && 'pr-10',
							Icon && 'pl-10',
							error && 'border-destructive',
							inputClassName,
						)}
						ref={ref}
						value={value ?? ''}
						placeholder={placeholder ? t(placeholder) : ''}
						{...props}
					/>

					{Icon && (
						<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
							<Icon className="h-5 w-5 text-muted-foreground" />
						</div>
					)}

					{isPassword && (
						<button
							type="button"
							onClick={() => setShowPassword(!showPassword)}
							className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
						>
							{showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
						</button>
					)}
				</div>

				{error && <small className="text-destructive text-xs">{error}</small>}
			</div>
		);
	},
);

Input.displayName = 'Input';

export { Input };
